import * as React from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import TextField from "@mui/material/TextField";
import { Box } from "@mui/material";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  allowNegative?: boolean;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, allowNegative, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        isAllowed={(values) => {
          const { value } = values;
          if (allowNegative) {
            return value === "" || value === "0" || /^-?\d*$/.test(value);
          }
          return value === "" || value === "0" || /^[1-9]\d*$/.test(value);
        }}
        thousandSeparator
        valueIsNumericString
        prefix="EGP "
        allowNegative={allowNegative}
      />
    );
  }
);

export interface MoneyInputProps {
  isError?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  defaultValue?: number | undefined;
  value?: number | string | undefined;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  negative?: boolean; // New prop to allow negative values
  size?: "small" | "medium";
}

export default function MoneyInput({
  isError,
  onChange,
  defaultValue,
  value,
  label,
  helperText,
  disabled,
  autoFocus,
  negative = false, // Default to false for backward compatibility
  size,
}: MoneyInputProps) {
  const [values, setValues] = React.useState({
    numberformat: defaultValue !== undefined ? defaultValue?.toString() : "",
  });

  React.useEffect(() => {
    if (defaultValue !== undefined) {
      setValues({
        numberformat: defaultValue?.toString(),
      });
    }
  }, [defaultValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.replace(/^EGP\s/, "");
    if (negative) {
      // Allow negative numbers when negative prop is true
      if (newValue === "" || newValue === "0" || /^-?\d*$/.test(newValue)) {
        setValues({
          ...values,
          [event.target.name]: newValue,
        });
        onChange(event);
      }
    } else {
      // Original positive-only validation
      if (newValue === "" || newValue === "0" || /^[1-9]\d*$/.test(newValue)) {
        setValues({
          ...values,
          [event.target.name]: newValue,
        });
        onChange(event);
      }
    }
  };

  const displayValue =
    value !== undefined && value !== 0 ? value : values.numberformat;

  return (
    <Box
      sx={{
        width: "100%",
        padding: "0",
        maxHeight: "70px",
        display: "flex",
        marginBottom: "0",
      }}
    >
      <TextField
        label={label}
        value={displayValue}
        onChange={onChange || handleChange}
        autoFocus={autoFocus}
        onFocus={(e) => {
          if (e.target.value === "Not Set") {
            e.target.value = "";
            setValues({
              ...values,
              numberformat: "",
            });
          }
        }}
        name="numberformat"
        id="formatted-numberformat-input"
        error={isError}
        helperText={isError && helperText ? helperText : ""}
        InputProps={{
          inputComponent: NumericFormatCustom as any,
          inputProps: {
            allowNegative: negative,
          },
        }}
        fullWidth
        disabled={disabled}
        size={size}
      />
    </Box>
  );
}
